/**
 * Integrytas Group - Dynamic Schema Evolution (DSE)
 * Page: DSE.REGISTRY
 *
 * Admin console for JSONB shadow-fields: enable an entity (adds ext_attributes +
 * GIN index), define per-key schemas (type/required/format/enum/range/indexed)
 * that the Common Routine validates on every write, and rebuild the flat
 * security_invoker view that exposes each key as a typed virtual column.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import type { AuthSession } from '../../types/auth';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DSE.REGISTRY';

interface EntityRow { dse_entity_id: number; entity_name: string; unknown_key_policy: 'ALLOW' | 'REJECT'; is_enabled: 'Y' | 'N' }
interface AttrRow {
  dse_attribute_def_id: number; entity_name: string; attribute_key: string; label: string | null;
  data_type: 'STRING' | 'INT' | 'NUMBER' | 'BOOL' | 'DATE' | 'DATETIME' | 'ENUM';
  is_required: 'Y' | 'N'; format_name: string | null; regex: string | null; allowed_values: unknown;
  min_value: number | null; max_value: number | null; min_len: number | null; max_len: number | null;
  is_indexed: 'Y' | 'N'; sort_order: number; is_active: 'Y' | 'N';
}

const TYPES = ['STRING', 'INT', 'NUMBER', 'BOOL', 'DATE', 'DATETIME', 'ENUM'] as const;

export interface DseRegistryPageProps { session: AuthSession }

export function DseRegistryPage({ session }: DseRegistryPageProps) {
  return (
    <PersonalizationProvider pageCode="DSE.REGISTRY" session={session}>
      <DseRegistryPageInner session={session} />
    </PersonalizationProvider>
  );
}

function DseRegistryPageInner({ session }: DseRegistryPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const ATTR_GRID_COLS: GridColumnDef[] = [
    { key: 'attribute_key', header: t('dse_registry.col.key', 'Key'),          defaultWidth: 150 },
    { key: 'label',         header: t('dse_registry.col.label', 'Label'),      defaultWidth: 130 },
    { key: 'data_type',     header: t('dse_registry.col.type', 'Type'),        defaultWidth: 110 },
    { key: 'is_required',   header: t('dse_registry.col.req', 'Req'),          defaultWidth: 60  },
    { key: 'format_name',   header: t('dse_registry.col.format', 'Format'),    defaultWidth: 120 },
    { key: 'enum_regex',    header: t('dse_registry.col.enum_regex', 'Enum / regex'), defaultWidth: 150 },
    { key: 'min',           header: t('dse_registry.col.min', 'Min'),          defaultWidth: 70  },
    { key: 'max',           header: t('dse_registry.col.max', 'Max'),          defaultWidth: 70  },
    { key: 'is_indexed',    header: t('dse_registry.col.idx', 'Idx'),          defaultWidth: 60  },
    { key: 'is_active',     header: t('dse_registry.col.on', 'On'),            defaultWidth: 60  },
  ];
  const [entities, setEntities] = useState<EntityRow[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [attrs, setAttrs] = useState<AttrRow[]>([]);
  // §17.B12: free-text search + Active facet over the attribute grid, defaulting
  // to Active-only on first load. Clear-all resets both back to "show everything".
  const [attrFilter, setAttrFilter] = useState('');
  const [facets, setFacets] = useState<{ active: string[] }>({ active: ['Y'] });
  const [newEntity, setNewEntity] = useState('');
  const [newPolicy, setNewPolicy] = useState<'ALLOW' | 'REJECT'>('ALLOW');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // §17.B13: related-children expand/contract. An attribute definition is itself a
  // child of its entity and has no child collections of its own — inert disclosure.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [
    // TODO: wire relations
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reloadEntities = async () => {
    const { data, error: e } = await apiDb.from('dse_entity').select('dse_entity_id, entity_name, unknown_key_policy, is_enabled').order('entity_name');
    if (e) { setError(e.message); return; }
    setEntities((data ?? []) as EntityRow[]);
  };
  const reloadAttrs = async (entity: string) => {
    const { data, error: e } = await apiDb.from('dse_attribute_def').select('dse_attribute_def_id, entity_name, attribute_key, label, data_type, is_required, format_name, regex, allowed_values, min_value, max_value, min_len, max_len, is_indexed, sort_order, is_active').eq('entity_name', entity).order('sort_order').order('attribute_key');
    if (e) { setError(e.message); return; }
    setAttrs((data ?? []) as AttrRow[]);
  };
  useEffect(() => { void reloadEntities(); }, []);
  useEffect(() => { if (sel) void reloadAttrs(sel); }, [sel]);

  const enableEntity = async () => {
    const name = newEntity.trim();
    if (!name) return;
    setBusy(true); setError(null); setNote(null);
    try {
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>('dse_enable_entity', { p_entity: name, p_policy: newPolicy });
      if (e) throw e;
      if (data && data.ok === false) { setError(data.error_code ?? t('dse_registry.enable_failed', 'Enable failed.')); return; }
      setNewEntity(''); await reloadEntities(); setSel(name);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  const rebuild = async (entity: string) => {
    setBusy(true); setError(null); setNote(null);
    try {
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string; view?: string; virtual_columns?: number; functional_indexes?: number }>('dse_rebuild_entity', { p_entity: entity });
      if (e) throw e;
      if (data?.ok === false) setError(data.error_code ?? t('dse_registry.rebuild_failed', 'Rebuild failed.'));
      else if (data) setNote(t('dse_registry.rebuild_done', 'Built view {{view}} · {{cols}} virtual columns · {{idx}} functional indexes.', { view: data.view, cols: data.virtual_columns, idx: data.functional_indexes }));
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  const addAttr = async () => {
    if (!sel) return;
    await routedInsert(session, 'dse_attribute_def', { entity_name: sel, attribute_key: `field_${Date.now()}`, data_type: 'STRING', is_required: 'N', is_indexed: 'N', is_active: 'Y' });
    await reloadAttrs(sel);
  };
  const saveAttr = async (r: AttrRow, allowedText: string) => {
    let allowed: unknown = null;
    if (allowedText.trim()) { try { allowed = JSON.parse(allowedText); } catch { setError(t('dse_registry.allowed_values_json', 'Allowed values must be a JSON array.')); return; } }
    const { error: e } = await routedUpdate(session, 'dse_attribute_def', 'dse_attribute_def_id', r.dse_attribute_def_id, {
      attribute_key: r.attribute_key.trim(), label: r.label?.trim() || null, data_type: r.data_type, is_required: r.is_required,
      format_name: r.format_name?.trim() || null, regex: r.regex?.trim() || null, allowed_values: allowed,
      min_value: r.min_value, max_value: r.max_value, min_len: r.min_len, max_len: r.max_len,
      is_indexed: r.is_indexed, sort_order: r.sort_order, is_active: r.is_active,
    });
    if (e) setError(e.message);
  };
  const delAttr = async (id: number) => { await routedDelete(session, 'dse_attribute_def', 'dse_attribute_def_id', id); setAttrs(a => a.filter(x => x.dse_attribute_def_id !== id)); };

  const patch = (i: number, p: Partial<AttrRow>) => setAttrs(a => a.map((x, j) => j === i ? { ...x, ...p } : x));

  const facetOptions = useMemo(() => ({
    active: [
      { value: 'Y', label: t('common.boolean.active', 'Active') },
      { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
    ],
  }), [t]);

  const anyFilterActive = facets.active.length > 0 || attrFilter.trim().length > 0;
  const clearAll = () => { setAttrFilter(''); setFacets({ active: [] }); };

  // A row passes when it matches every active facet (Active) and the free-text
  // search over the attribute's text columns (key / label). References from
  // `attrs` are preserved, so index-based `patch`/`saveAttr` stay correct.
  const filteredAttrs = useMemo(() => {
    const q = attrFilter.trim().toLowerCase();
    return attrs.filter(r =>
      (facets.active.length === 0 || facets.active.includes(r.is_active)) &&
      (q === '' ||
        r.attribute_key.toLowerCase().includes(q) ||
        (r.label ?? '').toLowerCase().includes(q)),
    );
  }, [attrs, attrFilter, facets]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('dse_registry.title', 'Shadow-Field Registry')}</span>
        <button onClick={() => void reloadEntities()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('dse_registry.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('dse_registry.working', 'Working…')}</span>}
        {note && <span className="text-csm-xs text-green-700">{note}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('dse_registry.error_banner', "Couldn't complete the last shadow-field operation.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => void reloadEntities()}
          details_label={t('common.details', 'Details')}
        />
      )}

      <div className="flex min-h-0 flex-1">
        {/* Entities */}
        <div className="flex w-72 shrink-0 flex-col border-r border-csm-border">
          <div className="flex flex-col gap-1 border-b border-csm-border-soft p-2">
            <span className="text-csm-2xs uppercase tracking-wide text-csm-watermark">{t('dse_registry.enable_entity', 'Enable an entity (table)')}</span>
            <input value={newEntity} onChange={e => setNewEntity(e.target.value)} placeholder={t('dse_registry.table_name_ph', 'table_name')} className="h-7 rounded border border-csm-border px-1 font-mono text-csm-xs" />
            <div className="flex gap-1">
              <select value={newPolicy} onChange={e => setNewPolicy(e.target.value as 'ALLOW' | 'REJECT')} className="h-7 flex-1 rounded border border-csm-border px-1 text-csm-xs" title={t('dse_registry.unknown_key_policy', 'unknown-key policy')}>
                <option value="ALLOW">{t('dse_registry.allow_unknown', 'ALLOW unknown')}</option>
                <option value="REJECT">{t('dse_registry.reject_unknown', 'REJECT unknown')}</option>
              </select>
              <button onClick={() => void enableEntity()} disabled={busy} className="h-7 rounded border border-primary bg-primary px-2 text-csm-xs text-white hover:opacity-90 disabled:opacity-50">{t('dse_registry.enable', 'Enable')}</button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {entities.map(en => (
              <button key={en.dse_entity_id} onClick={() => setSel(en.entity_name)}
                className={`flex w-full items-center gap-2 border-b border-csm-border-soft px-2 py-1 text-left hover:bg-csm-surface-alt ${sel === en.entity_name ? 'bg-csm-surface-alt' : ''}`}>
                <span className="font-mono text-csm-xs text-csm-ink">{en.entity_name}</span>
                <span className="ml-auto text-csm-2xs uppercase tracking-wide text-csm-watermark">{en.unknown_key_policy}</span>
                {en.is_enabled === 'Y' && <span className="rounded bg-green-100 px-1 text-csm-2xs text-green-800">{t('dse_registry.on', 'on')}</span>}
              </button>
            ))}
            {entities.length === 0 && <div className="p-3 text-csm-xs text-csm-watermark">{t('dse_registry.no_entities', 'No entities enabled. Enter a table name above and click Enable.')}</div>}
          </div>
        </div>

        {/* Attributes */}
        <div className="min-h-0 flex-1 overflow-auto p-3">
          {!sel && <div className="text-csm-sm text-csm-watermark">{t('dse_registry.select_entity', 'Select an entity to define its custom-field schema.')}</div>}
          {sel && (
            <>
              <div className="mb-2 flex items-center gap-2">
                <span className="font-mono text-csm-base text-csm-ink">{sel}</span>
                <span className="text-csm-2xs text-csm-watermark">{t('dse_registry.ext_attributes_schema', 'ext_attributes schema')}</span>
                <input
                  type="text"
                  value={attrFilter}
                  onChange={e => setAttrFilter(e.target.value)}
                  placeholder={t('dse_registry.search_ph', 'Search key / label')}
                  className="h-7 w-48 rounded border border-csm-border px-2 text-csm-xs focus:border-csm-watermark focus:outline-none"
                />
                <FacetFilter label={t('common.boolean.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
                <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
                {canAdd && (<button onClick={() => void addAttr()} className="ml-auto h-7 rounded border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-surface-alt">{t('dse_registry.add_attribute', '+ Attribute')}</button>)}
                {canChange && (<button onClick={() => void rebuild(sel)} disabled={busy} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-surface-alt disabled:opacity-50" title={t('dse_registry.rebuild_title', '(re)generate dse_<entity>_flat view + indexes')}>{t('dse_registry.rebuild_view', 'Rebuild view')}</button>)}
              </div>
              <div className="overflow-x-auto">
                <PersonalizedGrid componentKey="grid" columns={ATTR_GRID_COLS}>
                  {(visibleCols, widths) => (
                    <table className="w-full border-collapse text-csm-sm">
                      <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                        <th className="w-7 px-1 py-1" title={t('dse_registry.col.related', 'Related records')} />
                        {visibleCols.map(col => (
                          <th key={col.key} className="px-1 py-1"
                              style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                        ))}
                        <th className="px-1 py-1 text-right"></th>
                      </tr></thead>
                      <tbody>
                        {filteredAttrs.map(r => {
                          const i = attrs.indexOf(r);
                          return (
                          <Fragment key={r.dse_attribute_def_id}>
                          <tr className="border-b border-csm-border-soft">
                            <td className="px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                              <RelatedExpandButton
                                open={detail.isOpen(r.dse_attribute_def_id)}
                                hasChildren={hasRelated(r.dse_attribute_def_id)}
                                onToggle={() => detail.toggle(r.dse_attribute_def_id)}
                                expandLabel={t('dse_registry.related.expand', 'Show related records')}
                                collapseLabel={t('dse_registry.related.collapse', 'Hide related')}
                                tooltip={t('dse_registry.related.tooltip', 'Show related records')}
                              />
                            </td>
                            {visibleCols.map(col => (
                              <td key={col.key} className={`px-1 py-1${col.key === 'is_required' || col.key === 'is_indexed' || col.key === 'is_active' ? ' text-center' : ''}`}
                                  style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                                {col.key === 'attribute_key' && <input value={r.attribute_key} onChange={e => patch(i, { attribute_key: e.target.value })} onBlur={() => void saveAttr(attrs[i], JSON.stringify(attrs[i].allowed_values ?? null))} className="h-7 w-32 rounded border border-csm-border px-1 font-mono text-csm-xs" />}
                                {col.key === 'label' && <input value={r.label ?? ''} onChange={e => patch(i, { label: e.target.value })} onBlur={() => void saveAttr(attrs[i], JSON.stringify(attrs[i].allowed_values ?? null))} className="h-7 w-28 rounded border border-csm-border px-1 text-csm-xs" />}
                                {col.key === 'data_type' && <select value={r.data_type} onChange={e => patch(i, { data_type: e.target.value as AttrRow['data_type'] })} onBlur={() => void saveAttr(attrs[i], JSON.stringify(attrs[i].allowed_values ?? null))} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select>}
                                {col.key === 'is_required' && <input type="checkbox" checked={r.is_required === 'Y'} onChange={e => patch(i, { is_required: e.target.checked ? 'Y' : 'N' })} onBlur={() => void saveAttr(attrs[i], JSON.stringify(attrs[i].allowed_values ?? null))} />}
                                {col.key === 'format_name' && <input value={r.format_name ?? ''} onChange={e => patch(i, { format_name: e.target.value })} onBlur={() => void saveAttr(attrs[i], JSON.stringify(attrs[i].allowed_values ?? null))} placeholder={t('dse_registry.iso_date_ph', 'ISO_DATE')} className="h-7 w-24 rounded border border-csm-border px-1 font-mono text-csm-2xs" title={t('dse_registry.dqe_named_format', 'DQE named format')} />}
                                {col.key === 'enum_regex' && <input defaultValue={r.data_type === 'ENUM' ? JSON.stringify(r.allowed_values ?? []) : (r.regex ?? '')} onBlur={e => { if (r.data_type === 'ENUM') void saveAttr(attrs[i], e.target.value); else { patch(i, { regex: e.target.value }); void saveAttr({ ...attrs[i], regex: e.target.value }, JSON.stringify(attrs[i].allowed_values ?? null)); } }} placeholder={r.data_type === 'ENUM' ? t('dse_registry.enum_ph', '["A","B"]') : t('dse_registry.regex_ph', 'regex')} className="h-7 w-32 rounded border border-csm-border px-1 font-mono text-csm-2xs" />}
                                {col.key === 'min' && <input type="number" value={(r.data_type === 'INT' || r.data_type === 'NUMBER' ? r.min_value : r.min_len) ?? ''} onChange={e => patch(i, (r.data_type === 'INT' || r.data_type === 'NUMBER') ? { min_value: e.target.value === '' ? null : Number(e.target.value) } : { min_len: e.target.value === '' ? null : Number(e.target.value) })} onBlur={() => void saveAttr(attrs[i], JSON.stringify(attrs[i].allowed_values ?? null))} className="h-7 w-14 rounded border border-csm-border px-1 text-csm-2xs" />}
                                {col.key === 'max' && <input type="number" value={(r.data_type === 'INT' || r.data_type === 'NUMBER' ? r.max_value : r.max_len) ?? ''} onChange={e => patch(i, (r.data_type === 'INT' || r.data_type === 'NUMBER') ? { max_value: e.target.value === '' ? null : Number(e.target.value) } : { max_len: e.target.value === '' ? null : Number(e.target.value) })} onBlur={() => void saveAttr(attrs[i], JSON.stringify(attrs[i].allowed_values ?? null))} className="h-7 w-14 rounded border border-csm-border px-1 text-csm-2xs" />}
                                {col.key === 'is_indexed' && <input type="checkbox" checked={r.is_indexed === 'Y'} onChange={e => patch(i, { is_indexed: e.target.checked ? 'Y' : 'N' })} onBlur={() => void saveAttr(attrs[i], JSON.stringify(attrs[i].allowed_values ?? null))} title={t('dse_registry.functional_index', 'functional index')} />}
                                {col.key === 'is_active' && <input type="checkbox" checked={r.is_active === 'Y'} onChange={e => patch(i, { is_active: e.target.checked ? 'Y' : 'N' })} onBlur={() => void saveAttr(attrs[i], JSON.stringify(attrs[i].allowed_values ?? null))} />}
                              </td>
                            ))}
                            <td className="px-1 py-1 text-right">{canDelete && (<button onClick={() => void delAttr(r.dse_attribute_def_id)} className="text-csm-xs text-csm-danger">{t('dse_registry.del', 'del')}</button>)}</td>
                          </tr>
                          {detail.isOpen(r.dse_attribute_def_id) && (
                            <tr>
                              <td colSpan={visibleCols.length + 2} className="bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                                <RelatedChildrenPanel groups={buildRelatedGroups(r.dse_attribute_def_id)} session={session} />
                              </td>
                            </tr>
                          )}
                          </Fragment>
                          );
                        })}
                        {filteredAttrs.length === 0 && <tr><td colSpan={visibleCols.length + 2} className="px-2 py-8 text-center">
                          <EmptyState
                            variant={attrs.length === 0 ? 'no-data' : 'no-match'}
                            title={attrs.length === 0
                              ? t('dse_registry.no_custom_fields', 'No custom fields defined. Click “+ Attribute”.')
                              : t('dse_registry.no_match', 'No attributes match the current filters.')}
                            action={attrs.length === 0
                              ? canAdd && (<button onClick={() => void addAttr()} className="mt-2 h-7 rounded border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-surface-alt">{t('dse_registry.add_attribute', '+ Attribute')}</button>)
                              : <button onClick={clearAll} className="mt-2 h-7 rounded border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-surface-alt">{t('common.clear_filters', 'Clear filters')}</button>}
                          />
                        </td></tr>}
                      </tbody>
                    </table>
                  )}
                </PersonalizedGrid>
              </div>
              <p className="mt-3 text-csm-2xs text-csm-watermark">{t('dse_registry.footer_pre', 'After editing attributes, click “Rebuild view” to regenerate')} <span className="font-mono">dse_{sel}_flat</span> {t('dse_registry.footer_post', '(typed virtual columns + functional indexes) for Dynamic Data Views & reports.')}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
